import { Button, Grid, Typography } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { RegisteredTextField } from '../../../components/fields/TextField'
import { IssueLabel } from '../../../components/entities/IssueLabel'
import { RegisteredIntegerField } from '../../../components/fields/NumericField'
import { ConvertibleLabel } from '../../../components/entities/ConvertibleLabel'
import { IssueType } from '@finance-lab/utils/types'
import { FormOrigin } from '../types/FormWarrantEntity'

export function WarrantOriginsForm<T extends { id: string; type?: IssueType }>({
    warrantOrigin,
    entities,
    namePrefix,
    unlinkIssue,
}: {
    entities: Array<T>
    namePrefix: string
    warrantOrigin: FormOrigin
    unlinkIssue: (id: string) => void
}) {
    const formMethods = useFormContext()
    const entityInfo = (entity: { issueId: string | null; convertibleId: string | null }) =>
        entities.find((e) => e.id === entity?.issueId || e.id === entity?.convertibleId)
    const issue = entityInfo(warrantOrigin)
    const linkedWithEntity = (warrantOrigin: { issueId: string | null; convertibleId: string | null }) =>
        !!warrantOrigin.issueId || !!warrantOrigin.convertibleId
    const onClickUnlink = () => {
        unlinkIssue(warrantOrigin.id || warrantOrigin.tempId)
    }
    const showWarrantsPerUnit = issue?.type && ['Rights issue', 'IPO'].includes(issue.type)

    return (
        <>
            <Grid size={{ xs: 12, md: showWarrantsPerUnit ? 4 : 7 }} key={warrantOrigin.issueId}>
                {/* TODO: should be warrantOrigin.id. Also, should be on the Fragment element */}
                <Typography variant='body1'>
                    {linkedWithEntity(warrantOrigin) ? (
                        warrantOrigin.issueId ? (
                            <IssueLabel issue={entityInfo(warrantOrigin)} adminLink />
                        ) : (
                            <ConvertibleLabel convertible={entityInfo(warrantOrigin)} adminLink />
                        )
                    ) : null}
                    {!linkedWithEntity(warrantOrigin) && (
                        <RegisteredTextField
                            name={`${namePrefix}externalComment`}
                            label='Ursprungsbeskrivning'
                            fullWidth
                            multiline
                            minRows={2}
                        />
                    )}
                </Typography>
            </Grid>

            {showWarrantsPerUnit && (
                <Grid size={{ xs: 12, md: 3 }}>
                    <RegisteredIntegerField
                        name={`${namePrefix}warrantsPerUnit`}
                        label='Teckningsoptioner per unit'
                        validation={{
                            validate: (warUnit) => {
                                const numberOfWarrants = formMethods.getValues(`${namePrefix}numberOfWarrants`)
                                if (numberOfWarrants && warUnit) {
                                    return (
                                        parseFloat(warUnit) < parseFloat(numberOfWarrants) ||
                                        'Antal per unit måste vara mindre än totalt antal optioner'
                                    )
                                } else {
                                    return true
                                }
                            },
                        }}
                        fullWidth
                    />
                </Grid>
            )}
            <Grid size={{ xs: 12, md: 3 }}>
                <RegisteredIntegerField
                    name={`${namePrefix}numberOfWarrants`}
                    label='Teckningsoptioner'
                    allowNegative
                    validation={{
                        validate: (numWarrants) => {
                            const warrantsPerUnit = formMethods.getValues(`${namePrefix}warrantsPerUnit`)
                            if (warrantsPerUnit && numWarrants) {
                                return (
                                    parseFloat(numWarrants) > parseFloat(warrantsPerUnit) ||
                                    'Totalt antal optioner måste vara större än antal per unit'
                                )
                            } else {
                                return true
                            }
                        },
                    }}
                    fullWidth
                />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
                <Button variant='outlined' color='primary' fullWidth onClick={onClickUnlink}>
                    Koppla bort
                </Button>
            </Grid>
        </>
    )
}
